import {
  ErrorHandler,
  Injectable,
  InjectionToken,
  LOCALE_ID,
  NgModule,
  inject,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app.routing';
import { CoreModule } from '@core/core.module';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LayoutModule } from './layout/layout.module';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { EffectsNgModule } from '@ngneat/effects-ng';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { SessionPinInterceptor } from '@core/interceptor/session-pin/session-pin.interceptor';
import { environment } from '../environments/environment';
import { UserEffects } from '@data/user/user.effects';
import { SessionEffects } from '@data/session/session.effects';
import * as de from '@angular/common/locales/de';

import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { StopEffects } from '@data/stop/stop.effects';
import { NgChartsModule } from 'ng2-charts';
import { JobGeneralEffects } from '@data/job-general/job-general.effects';
import { TeamEffects } from '@data/team/team.effects';
import { registerLocaleData } from '@angular/common';
import { RollbarErrorHandler, RollbarService, rollbarFactory } from '@core/rollbar';
import { SessionJobDatasetEffects } from '@data/session-job-dataset/session-job-dataset.effects';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const DATE_FORMAT = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    EffectsNgModule.forRoot(
      [
        UserEffects,
        TeamEffects,
        SessionEffects,
        SessionJobDatasetEffects,
        StopEffects,
        JobGeneralEffects,
      ],
      {
        dispatchByDefault: false,
      },
    ),
    AuthModule.forRoot({
      ...environment.auth,
      authorizationParams: {
        ...environment.auth.authorizationParams,
        // email and password only: the tenant's Google login runs on Auth0 dev keys
        connection: 'Username-Password-Authentication',
      },
      useRefreshTokens: true,
      // A missing or rotated-out refresh token must not hard-fail every protected request —
      // fall back to silent iframe auth and let the guard redirect to login if that fails too.
      useRefreshTokensFallback: true,
      cacheLocation: 'localstorage',
      httpInterceptor: {
        allowedList: [
          {
            uri: `${environment.apiUrl}/private/*`,
            allowAnonymous: false,
          },
          {
            uri: `${environment.apiUrl}/public/*`,
            allowAnonymous: true,
          },
        ],
      },
    }),
    BrowserModule,
    MatDatepickerModule,
    MatMomentDateModule,
    BrowserAnimationsModule,
    NgChartsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),

    LayoutModule,
    CoreModule,

    AppRoutingModule,

    environment.production ? [] : AkitaNgDevtools.forRoot(),
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SessionPinInterceptor, multi: true },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    registerLocaleData(de.default);
  }
}
